import { useQuery } from '@tanstack/react-query';
import axiosInstance from '@/dataProvider/axios';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import {
  ORG_USAGE_REPORT_ENDPOINT,
  ORG_USAGE_REPORT_MONTH_ENDPOINT,
  isMonthSelection,
  type UsageWindowSelection,
} from '@/services/org-admin';
import type { OrgUsageReport } from '@/services/org-admin';

// Re-exported from the service layer, which is where these now live — the pure adapters need them
// and cannot import from a hook. Kept here so every existing importer keeps working.
export type { UsageWindowSelection };
export { isMonthSelection };


// Admin-only upstream (403 for a plain member) — callers must pass `enabled: false`
// for non-admins rather than relying on this hook to know the caller's role.
export function useOrgUsageReport(
  selection: UsageWindowSelection,
  options?: { enabled?: boolean; refetchInterval?: number | false },
) {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);

  return useQuery({
    queryKey: ['org-usage-report', orgId, selection],
    queryFn: async () => {
      const url = isMonthSelection(selection)
        ? ORG_USAGE_REPORT_MONTH_ENDPOINT(orgId!, selection)
        : ORG_USAGE_REPORT_ENDPOINT(orgId!, selection);
      const { data } = await axiosInstance.get<OrgUsageReport>(url);
      return data;
    },
    enabled: !!orgId && (options?.enabled ?? true),
    /*
     * `refetchInterval` is for Billing's top-up settling window. A seat top-up's money
     * arrives as that seat's `budget_usd`, so the payload carrying budget is the one that
     * has to be polled while Stripe settles — and Billing reads budget AND spend from here
     * now, so the poll belongs here rather than on the `/usage` rollup it used to use.
     */
    refetchInterval: options?.refetchInterval ?? false,
    // Cached data still shows instantly on mount, but every visit to the Usage
    // tab re-fetches regardless of the global 1-minute staleTime — admins land
    // here specifically to check current spend/budget health.
    refetchOnMount: 'always',
  });
}
